import { supabase } from './supabase';

export interface BookingWorkflowStep {
  id: string;
  booking_workflow_id: string;
  workflow_step_id: string;
  assigned_resource_id?: string;
  title: string;
  order_index: number;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  completed_at?: string;
  notes?: string;
}

export async function updateWorkflowStepStatus(
  stepId: string,
  status: 'pending' | 'in_progress' | 'completed' | 'skipped',
  notes?: string
) {
  // First, get the step details to find related steps
  const { data: stepData, error: stepError } = await supabase
    .from('booking_workflow_steps')
    .select('booking_workflow_id, workflow_step_library_id, title')
    .eq('id', stepId)
    .single();
    
  if (stepError) {
    console.error('Error fetching workflow step:', stepError);
    throw stepError;
  }
  
  const updateData: any = {
    status,
    updated_at: new Date().toISOString()
  };

  // If completing the step, add completed_at timestamp
  if (status === 'completed') {
    updateData.completed_at = new Date().toISOString();
  }

  // Add notes if provided
  if (notes !== undefined) {
    updateData.notes = notes;
  }

  // If completing a step, complete all resources assigned to the same step
  if (status === 'completed' && stepData) {
    // Find all steps in the same workflow with the same library ID or title
    let query = supabase
      .from('booking_workflow_steps')
      .update(updateData)
      .eq('booking_workflow_id', stepData.booking_workflow_id);
      
    // Match by workflow_step_library_id if available, otherwise by title
    if (stepData.workflow_step_library_id) {
      query = query.eq('workflow_step_library_id', stepData.workflow_step_library_id);
    } else {
      query = query.eq('title', stepData.title);
    }
    
    const { data, error } = await query.select();
    
    if (error) {
      console.error('Error updating all related workflow steps:', error);
      throw error;
    }
    
    console.log(`Updated ${data?.length || 0} workflow steps to completed status`);
    return data;
  } else {
    // For non-completion statuses, only update the specific step
    const { data, error } = await supabase
      .from('booking_workflow_steps')
      .update(updateData)
      .eq('id', stepId)
      .select()
      .single();

    if (error) {
      console.error('Error updating workflow step status:', error);
      throw error;
    }
    
    return data;
  }
}

export async function canResourceUpdateStep(
  workflowStepId: string,
  resourceId: string
): Promise<boolean> {
  // Check if the resource is assigned to this workflow step
  const { data, error } = await supabase
    .from('booking_workflow_steps')
    .select('id')
    .eq('id', workflowStepId)
    .eq('assigned_resource_id', resourceId)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
    console.error('Error checking resource assignment:', error);
    return false;
  }

  return !!data;
}

export async function getIncompleteWorkflowSteps(bookingId: string): Promise<number> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log(`[WORKFLOW CHECK] Checking incomplete steps for booking ${bookingId}`);

  // First get all workflows for this booking
  const { data: workflows, error: workflowError } = await supabase
    .from('booking_workflows')
    .select('id')
    .eq('booking_id', bookingId);

  if (workflowError) {
    console.error('Error fetching booking workflows:', workflowError);
    return 0;
  }

  if (!workflows || workflows.length === 0) {
    console.log('[WORKFLOW CHECK] No workflows found for this booking');
    return 0;
  }

  console.log(`[WORKFLOW CHECK] Found ${workflows.length} workflows for booking`);

  // Now get all workflow steps that are not completed
  const { data: incompleteSteps, error: stepsError } = await supabase
    .from('booking_workflow_steps')
    .select('id, status')
    .in('booking_workflow_id', workflows.map(w => w.id))
    .in('status', ['pending', 'in_progress']);

  if (stepsError) {
    console.error('Error fetching incomplete workflow steps:', stepsError);
    return 0;
  }

  const incompleteCount = incompleteSteps?.length || 0;
  console.log(`[WORKFLOW CHECK] Found ${incompleteCount} incomplete workflow steps`);

  return incompleteCount;
}

export async function completeAllWorkflowSteps(bookingId: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log(`[WORKFLOW COMPLETE ALL] Starting completion for booking ${bookingId}`);

  // First get all workflows for this booking
  const { data: workflows, error: workflowError } = await supabase
    .from('booking_workflows')
    .select('id')
    .eq('booking_id', bookingId);

  if (workflowError) {
    console.error('Error fetching booking workflows:', workflowError);
    throw workflowError;
  }

  if (!workflows || workflows.length === 0) {
    console.log('[WORKFLOW COMPLETE ALL] No workflows found for this booking');
    return;
  }

  console.log(`[WORKFLOW COMPLETE ALL] Found ${workflows.length} workflows`);

  // Get all incomplete workflow steps
  const { data: incompleteSteps, error: stepsError } = await supabase
    .from('booking_workflow_steps')
    .select('id, title, status')
    .in('booking_workflow_id', workflows.map(w => w.id))
    .in('status', ['pending', 'in_progress']);

  if (stepsError) {
    console.error('Error fetching incomplete workflow steps:', stepsError);
    throw stepsError;
  }

  if (!incompleteSteps || incompleteSteps.length === 0) {
    console.log('[WORKFLOW COMPLETE ALL] No incomplete steps found');
    return;
  }

  console.log(`[WORKFLOW COMPLETE ALL] Found ${incompleteSteps.length} incomplete steps to complete`);

  // Update all steps to completed
  const { error: updateError } = await supabase
    .from('booking_workflow_steps')
    .update({ 
      status: 'completed',
      completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    })
    .in('id', incompleteSteps.map(step => step.id));

  if (updateError) {
    console.error('Error updating workflow steps:', updateError);
    throw updateError;
  }

  console.log('[WORKFLOW COMPLETE ALL] Successfully completed all workflow steps');
}

export async function getWorkflowStepsByBookingWorkflowId(
  bookingWorkflowId: string
) {
  const { data, error } = await supabase
    .from('booking_workflow_steps')
    .select(`
      *,
      resource:resources(
        id,
        first_name,
        last_name,
        avatar_url
      )
    `)
    .eq('booking_workflow_id', bookingWorkflowId)
    .order('order_index');

  if (error) {
    console.error('Error fetching workflow steps:', error);
    throw error;
  }

  return data;
}

export async function createBookingWorkflowWithSteps(
  bookingId: string,
  workflowId: string,
  businessId: string,
  steps: Array<{
    workflow_step_id: string;
    assigned_resource_id?: string;
    title: string;
    order_index: number;
  }>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Call the RPC function to create booking workflow with steps
  const { error } = await supabase.rpc('create_booking_workflow_with_steps', {
    p_booking_id: bookingId,
    p_workflow_id: workflowId,
    p_business_id: businessId,
    p_steps: steps
  });

  if (error) {
    console.error('Error creating booking workflow:', error);
    throw error;
  }
}

// New function that creates booking workflows without hard dependency on service workflows
export async function createBookingWorkflowWithStepsV2(
  bookingId: string,
  businessId: string,
  workflowName: string,
  serviceId: string,
  steps: Array<{
    workflow_step_library_id: string | null;
    assigned_resource_id?: string;
    title: string;
    description?: string;
    order_index: number;
  }>
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  // Check if the new RPC function exists
  try {
    const { data, error } = await supabase.rpc('create_booking_workflow_with_steps_v2', {
      p_booking_id: bookingId,
      p_business_id: businessId,
      p_workflow_name: workflowName,
      p_service_id: serviceId,
      p_steps: steps.map(step => ({
        ...step,
        workflow_step_library_id: step.workflow_step_library_id || null
      }))
    });

    if (error) {
      // If the new function doesn't exist, fall back to the old one with adapted parameters
      if (error.message?.includes('function') && error.message?.includes('does not exist')) {
        console.warn('New workflow function not found, using legacy approach');
        // For backward compatibility, use the old function but without workflow_step_id
        const legacySteps = steps.map(step => ({
          workflow_step_id: step.workflow_step_library_id, // Use library ID as step ID for now
          assigned_resource_id: step.assigned_resource_id,
          title: step.title,
          order_index: step.order_index
        }));
        
        // Create a dummy workflow ID for the old function
        const dummyWorkflowId = '00000000-0000-0000-0000-000000000000';
        
        return createBookingWorkflowWithSteps(
          bookingId,
          dummyWorkflowId,
          businessId,
          legacySteps
        );
      }
      throw error;
    }
  } catch (error) {
    console.error('Error creating booking workflow:', error);
    throw error;
  }
}

/**
 * Delete booking workflows associated with a specific service
 * @param bookingId - The booking ID
 * @param serviceId - The service ID to remove workflows for
 */
export async function deleteBookingWorkflowsByServiceId(
  bookingId: string,
  serviceId: string
): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log(`[WORKFLOW CLEANUP] Deleting workflows for booking ${bookingId}, service ${serviceId}`);

  try {
    // First get all workflows for this booking and service
    const { data: workflows, error: workflowError } = await supabase
      .from('booking_workflows')
      .select('id')
      .eq('booking_id', bookingId)
      .eq('service_id', serviceId);

    if (workflowError) {
      console.error('Error fetching workflows to delete:', workflowError);
      throw workflowError;
    }

    if (!workflows || workflows.length === 0) {
      console.log('[WORKFLOW CLEANUP] No workflows found to delete');
      return;
    }

    const workflowIds = workflows.map(w => w.id);
    console.log(`[WORKFLOW CLEANUP] Found ${workflowIds.length} workflows to delete:`, workflowIds);

    // Delete workflow steps first (due to foreign key constraints)
    const { error: stepsError } = await supabase
      .from('booking_workflow_steps')
      .delete()
      .in('booking_workflow_id', workflowIds);

    if (stepsError) {
      console.error('Error deleting workflow steps:', stepsError);
      throw stepsError;
    }

    console.log(`[WORKFLOW CLEANUP] Deleted workflow steps for ${workflowIds.length} workflows`);

    // Then delete the workflows themselves
    const { error: workflowsError } = await supabase
      .from('booking_workflows')
      .delete()
      .in('id', workflowIds);

    if (workflowsError) {
      console.error('Error deleting workflows:', workflowsError);
      throw workflowsError;
    }

    console.log(`[WORKFLOW CLEANUP] Successfully deleted ${workflowIds.length} workflows and their steps`);
  } catch (error) {
    console.error('[WORKFLOW CLEANUP] Error cleaning up workflows:', error);
    throw error;
  }
}